import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fontesApi, type Fonte, type TipoFonte } from '@/lib/api/oto';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros, type BairroView } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Anexos } from '@/modules/gab/anexos/Anexos';
import { Info } from '@/design-system/Info';

const TIPOS: Array<{ v: TipoFonte; l: string }> = [
  { v: 'pesquisa', l: 'Pesquisa' },
  { v: 'noticia', l: 'Notícia / jornal' },
  { v: 'relatorio_publico', l: 'Relatório público' },
  { v: 'discurso', l: 'Discurso' },
  { v: 'documento_interno', l: 'Documento interno' },
];

const rotuloTipo = (t: TipoFonte) => TIPOS.find((x) => x.v === t)?.l || t;

/**
 * Marcação de arquivamento do protótipo (`classificarFonte`, proto:1523:
 * `ok:false` → "fica arquivada; nunca orienta estratégia local").
 *
 * Aqui é um valor que o GABINETE grava em `oto.fonte.escopo` — não uma
 * classificação que alguém calculou. Mora no campo de abrangência porque é
 * exatamente isso que ele guarda: onde a fonte vale. Não é vocabulário de
 * território (esse vem de `useBairros`), é o oposto dele.
 */
const FORA_DE_ESCOPO = 'Fora de escopo';

/** Sem acento, sem pontuação, minúsculo — para comparar nome com catálogo. */
const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Sugere a abrangência a partir do NOME que o usuário digitou.
 *
 * O protótipo chama isto de "Detectar automaticamente" e o implementa em
 * `classificarFonte(nome)` (proto:1523) — casamento de string sobre o nome do
 * arquivo, nada além disso. O rótulo é que promete mais do que a função faz:
 * quem lê "detectar" entende que o sistema abriu o material. Não abriu, e não
 * tem como abrir — `gab/entities/anexo.entity.ts` declara que nada no servidor
 * lê o conteúdo de um anexo (não há leitor de PDF/DOCX; `LlmService` só manda
 * texto para a API de chat).
 *
 * O que sobra é real e útil, e é o que está implementado: comparar o texto
 * digitado com o catálogo de territórios do mandato (`useBairros` — nomes,
 * municípios e UFs vindos do banco). Sem casamento, devolve `null`: um palpite
 * seria pior que a ausência.
 */
export function sugerirAbrangencia(
  nome: string,
  bairros: BairroView[],
): { valor: string; motivo: string } | null {
  const n = normalizar(nome);
  const tokens = new Set(n.split(/[^a-z0-9]+/).filter(Boolean));

  const bairro = bairros.find((b) => b.n && normalizar(b.n).length >= 4 && n.includes(normalizar(b.n)));
  if (bairro) return { valor: bairro.n, motivo: `território "${bairro.n}" do catálogo do mandato` };

  const municipios = Array.from(new Set(bairros.map((b) => b.municipio).filter(Boolean)));
  const municipio = municipios.find((m) => normalizar(m).length >= 4 && n.includes(normalizar(m)));
  if (municipio) return { valor: municipio, motivo: `município "${municipio}" do catálogo do mandato` };

  const ufs = Array.from(new Set(bairros.map((b) => b.uf).filter(Boolean)));
  const uf = ufs.find((u) => tokens.has(normalizar(u)));
  if (uf) return { valor: uf, motivo: `UF "${uf}" do catálogo do mandato` };

  return null;
}

export function FontesPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEscopo, setFiltroEscopo] = useState('');
  const [modal, setModal] = useState<Fonte | 'nova' | null>(null);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const filtros = { tipo: filtroTipo || undefined, escopo: filtroEscopo || undefined };

  const { data: fontes = [], isLoading } = useQuery({
    queryKey: ['fontes', filtros],
    queryFn: () => fontesApi.list(token, filtros),
    enabled: !!token,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['fontes'] });

  const criar = useMutation({
    mutationFn: (dados: Partial<Fonte>) => fontesApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      // Dizer que "o OTO passa a considerá-la" era falso: o gerador de
      // estratégias grava `fontes_usadas: []` (estrategias.service.ts) e nunca
      // consulta `oto.fonte`.
      toast('Fonte registrada no acervo do gabinete.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao registrar a fonte.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Fonte> }) =>
      fontesApi.update(id, dados, token),
    onSuccess: () => {
      invalidar();
      toast('Fonte atualizada.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a fonte.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => fontesApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Fonte removida do acervo.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a fonte.')),
  });

  /**
   * `oto.fonte.processada` é um booleano que o gabinete marca no `<select>`
   * "Revisão" deste mesmo drawer. Não há job, fila nem leitor por trás dele —
   * por isso a tela conta "marcadas como revisadas", e não "processadas".
   */
  const revisadas = fontes.filter((f) => f.processada).length;
  const escopos = new Set(fontes.map((f) => f.escopo).filter(Boolean));
  const anos = fontes.map((f) => f.ano).filter((a): a is number => !!a);

  return (
    <div className="page" id="p-fontes">
      <div className="head">
        <div>
          <h1 style={{ color: '#B26A00' }}>Fontes &amp; conhecimento</h1>
          <p>
            Acervo do gabinete: pesquisas de qualquer ano e lugar, jornais, relatórios públicos e
            discursos. Abrangência, resumo e uso são informados por você, e o arquivo fica
            guardado para download.
          </p>
          {/*
            Lacuna declarada, com a causa raiz nomeada. O protótipo dizia que
            "o OTO classifica a abrangência e usa cada fonte onde fizer
            sentido"; nenhuma das duas metades acontece.
          */}
          <div className="note warn" style={{ fontSize: 12, marginTop: 8 }}>
            <b>O acervo não alimenta as estratégias.</b> O que a fonte diz, para o sistema, é o
            que você escreve no campo de resumo.
            <Info titulo="Causa raiz de o acervo não alimentar a geração">
              O gerador (<span className="mono">oto/services/estrategias.service.ts</span>) grava{' '}
              <span className="mono">fontes_usadas: []</span> e nunca consulta{' '}
              <span className="mono">oto.fonte</span>: ele vê a persona e o mandato, mais nada. O
              conteúdo do arquivo também não é lido em lugar nenhum, porque não existe extração
              de texto no servidor.
            </Info>
          </div>
        </div>
        <div className="act">
          <button className="btn pri" onClick={() => setModal('nova')}>
            + Nova fonte
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi" data-testid="kpi-cadastradas">
          <div className="l">Fontes cadastradas</div>
          <div className="v">{fontes.length}</div>
          <div className="d">
            {revisadas} {revisadas === 1 ? 'marcada' : 'marcadas'} como revisada pelo gabinete
          </div>
        </div>
        <div className="card kpi">
          <div className="l">Escopos cobertos</div>
          <div className="v">{escopos.size}</div>
          <div className="d">municípios e territórios</div>
        </div>
        <div className="card kpi">
          <div className="l">Série histórica</div>
          <div className="v" style={{ fontSize: 20 }}>
            {anos.length ? `${Math.min(...anos)} – ${Math.max(...anos)}` : '—'}
          </div>
          <div className="d">amplitude dos dados carregados</div>
        </div>
        <div className="card kpi" data-testid="kpi-nao-revisadas">
          <div className="l">Sem marcação de revisão</div>
          <div className="v" style={{ color: fontes.length - revisadas ? 'var(--warn)' : 'var(--ink)' }}>
            {fontes.length - revisadas}
          </div>
          <div className="d">marcação manual, feita na ficha</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <select className="sel" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {TIPOS.map((t) => (
            <option key={t.v} value={t.v}>
              {t.l}
            </option>
          ))}
        </select>
        <select className="sel" value={filtroEscopo} onChange={(e) => setFiltroEscopo(e.target.value)}>
          <option value="">Todos os escopos</option>
          {bairros.map((b) => (
            <option key={b.id} value={b.n}>
              {b.n}
            </option>
          ))}
        </select>
      </div>

      {/* Lista */}
      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th>Fonte</th>
              <th>Tipo</th>
              <th>Ano</th>
              <th>Abrangência</th>
              {/*
                O conteúdo do campo não mudou: continua sendo o `resumo`. O que
                mudou é a autoria declarada — ele é digitado no `<textarea>`
                deste drawer, não extraído de coisa nenhuma.
              */}
              <th>Resumo informado pelo gabinete</th>
              <th>Revisão</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {fontes.map((f) => (
              <tr key={f.id} className="row">
                <td>
                  <b className="mono" style={{ fontSize: 12 }}>
                    {f.nome}
                  </b>
                  {f.url && (
                    <div>
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 11, color: 'var(--accent)' }}
                      >
                        abrir original
                      </a>
                    </div>
                  )}
                </td>
                <td>
                  <span className="chip neutral">{rotuloTipo(f.tipo)}</span>
                </td>
                <td className="num">{f.ano || '—'}</td>
                <td>
                  {f.escopo ? (
                    <span className={`chip ${f.escopo === FORA_DE_ESCOPO ? 'crit' : 'eleg'}`}>
                      {f.escopo}
                    </span>
                  ) : (
                    '—'
                  )}
                  {f.cargo && (
                    <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{f.cargo}</div>
                  )}
                </td>
                <td style={{ fontSize: 12, maxWidth: 320 }}>
                  {f.resumo || <span style={{ color: 'var(--ink3)' }}>—</span>}
                  {f.uso && (
                    <div style={{ color: 'var(--ink3)', marginTop: 4 }}>
                      <b>Uso:</b> {f.uso}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`chip ${f.processada ? 'good' : 'warn'}`}>
                    {f.processada ? 'revisada' : 'não revisada'}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Link className="btn sm" to="/perfil" title="Comparar com hoje">
                    ⇄ Hoje
                  </Link>{' '}
                  <button className="btn sm" onClick={() => setModal(f)}>
                    ✏️ Editar
                  </button>{' '}
                  <button
                    className="btn sm"
                    style={{ color: 'var(--crit)' }}
                    disabled={excluir.isPending}
                    onClick={() => {
                      if (window.confirm(`Remover a fonte "${f.nome}"?`)) excluir.mutate(f.id);
                    }}
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))}

            {isLoading && (
              <tr>
                <td colSpan={7} className="empty">
                  Carregando fontes...
                </td>
              </tr>
            )}
            {!isLoading && fontes.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  Nenhuma fonte com os filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <ModalFonte
          fonte={modal === 'nova' ? null : modal}
          bairros={bairros}
          salvando={criar.isPending || atualizar.isPending}
          onCancelar={() => setModal(null)}
          onSalvar={(dados) => {
            if (modal === 'nova') criar.mutate(dados);
            else atualizar.mutate({ id: modal.id, dados });
          }}
        />
      )}
    </div>
  );
}

function ModalFonte({
  fonte,
  bairros,
  salvando,
  onCancelar,
  onSalvar,
}: {
  fonte: Fonte | null;
  /** Catálogo real de territórios — fonte única de nome, município e UF. */
  bairros: BairroView[];
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: Partial<Fonte>) => void;
}) {
  const anoAtual = new Date().getFullYear();
  const [sugestao, setSugestao] = useState<string | null>(null);
  const [nome, setNome] = useState(fonte?.nome || '');
  const [tipo, setTipo] = useState<TipoFonte>(fonte?.tipo || 'pesquisa');
  const [ano, setAno] = useState(fonte?.ano ?? anoAtual);
  const [escopo, setEscopo] = useState(fonte?.escopo || '');
  const [cargo, setCargo] = useState(fonte?.cargo || '');
  const [resumo, setResumo] = useState(fonte?.resumo || '');
  const [uso, setUso] = useState(fonte?.uso || '');
  const [url, setUrl] = useState(fonte?.url || '');
  const [processada, setProcessada] = useState(fonte?.processada ?? false);

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast('O nome da fonte é obrigatório.');
      return;
    }
    if (ano > anoAtual + 1) {
      toast('O ano não pode ser no futuro.');
      return;
    }
    onSalvar({
      nome: nome.trim(),
      tipo,
      ano,
      escopo: escopo.trim() || undefined,
      cargo: cargo.trim() || undefined,
      resumo: resumo.trim() || undefined,
      uso: uso.trim() || undefined,
      url: url.trim() || undefined,
      processada,
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={fonte ? 'Editar fonte' : 'Nova fonte de conhecimento'}
        aoFechar={onCancelar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : fonte ? 'Salvar alterações' : 'Registrar fonte'}
            </button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="ft-nome">Nome / arquivo *</label>
          <input
            id="ft-nome"
            className="in"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: pesquisa-datafolha-2026.pdf"
            required
          />
        </div>

        <div className="grid g3">
          <div className="field">
            <label>Tipo</label>
            <select className="in" value={tipo} onChange={(e) => setTipo(e.target.value as TipoFonte)}>
              {TIPOS.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.l}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Ano</label>
            <input
              type="number"
              min={1900}
              max={anoAtual + 1}
              className="in num"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value) || anoAtual)}
            />
          </div>
          <div className="field">
            <label>Cargo de referência</label>
            <input
              className="in"
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
              placeholder="vereador"
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="ft-escopo">Abrangência</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              id="ft-escopo"
              className="in"
              style={{ flex: 1 }}
              list="escopos-disponiveis"
              value={escopo}
              onChange={(e) => {
                setEscopo(e.target.value);
                setSugestao(null);
              }}
              placeholder="Município, território ou “Fora de escopo”"
            />
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                const s = sugerirAbrangencia(nome, bairros);
                if (!s) {
                  setSugestao(
                    'Nenhum território, município ou UF do catálogo aparece no nome digitado, então nada foi preenchido.',
                  );
                  return;
                }
                setEscopo(s.valor);
                setSugestao(`Preenchido com o ${s.motivo}.`);
              }}
            >
              Sugerir abrangência
            </button>
          </div>
          <datalist id="escopos-disponiveis">
            {bairros.map((b) => (
              <option key={b.id} value={b.n} />
            ))}
            {Array.from(new Set(bairros.map((b) => b.municipio).filter(Boolean))).map((m) => (
              <option key={`mun-${m}`} value={m} />
            ))}
            <option value={FORA_DE_ESCOPO} />
          </datalist>
          {/*
            Rótulo trocado com motivo. O protótipo oferece "Detectar
            automaticamente" (proto:1577); a função por trás (`classificarFonte`,
            proto:1523) só compara strings com o NOME do arquivo. Chamar isso de
            detecção sugere que o material foi aberto — não foi, e não há como:
            nada no servidor lê o conteúdo de um anexo.
          */}
          <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
            A sugestão compara o <b>nome que você digitou</b> com o catálogo de territórios do
            mandato. O arquivo não é aberto nem lido: não existe extração de texto no servidor.{' '}
            <b>Fora de escopo</b> é uma marcação sua: arquiva a fonte como referência.
          </small>
          {sugestao && (
            <small style={{ fontSize: 11, color: 'var(--ink2)' }} role="status">
              {sugestao}
            </small>
          )}
        </div>

        <div className="field">
          <label htmlFor="ft-resumo">Resumo (escrito por você)</label>
          <textarea
            id="ft-resumo"
            className="in"
            style={{ minHeight: 70 }}
            value={resumo}
            onChange={(e) => setResumo(e.target.value)}
            placeholder="Os números e achados principais"
          />
          <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
            É este texto que a lista mostra na coluna de resumo. Nada é preenchido a partir do
            arquivo.
          </small>
        </div>

        <div className="field">
          <label htmlFor="ft-uso">Anotação de uso (para a equipe)</label>
          <textarea
            id="ft-uso"
            className="in"
            style={{ minHeight: 60 }}
            value={uso}
            onChange={(e) => setUso(e.target.value)}
            placeholder="Ex: entra como série histórica comparável por tema"
          />
          <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
            Fica registrado na ficha e é lido por pessoas. O gerador de estratégias não consulta
            este campo: ele grava <span className="mono">fontes_usadas: []</span>.
          </small>
        </div>

        <div className="grid g2">
          <div className="field">
            <label>URL do original</label>
            <input
              className="in"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div className="field">
            <label htmlFor="ft-revisao">Revisão (marcação manual)</label>
            <select
              id="ft-revisao"
              className="in"
              value={processada ? '1' : '0'}
              onChange={(e) => setProcessada(e.target.value === '1')}
            >
              <option value="0">Não revisada</option>
              <option value="1">Revisada pelo gabinete</option>
            </select>
          </div>
        </div>

        {/*
          Upload real, pelo mesmo componente das solicitações e visitas:
          `dono_tipo = 'fonte'` já existe no servidor (`anexo.entity.ts`,
          `DonoAnexo`). Ele exige o id do dono, que só existe depois do POST —
          por isso o campo aparece na edição e a nova fonte diz o porquê, em vez
          de oferecer um `<input type="file">` que não teria para onde enviar.
        */}
        {fonte ? (
          <div data-testid="anexos-fonte">
            <Anexos
              donoTipo="fonte"
              donoId={fonte.id}
              tipoPadrao="Pesquisa"
              titulo="Arquivo da fonte"
              ajuda="O arquivo fica guardado e pode ser baixado. O conteúdo não é lido pelo sistema."
            />
          </div>
        ) : (
          <div className="note" style={{ fontSize: 12 }}>
            <b>Arquivo:</b> registre a fonte primeiro. O anexo é gravado com o id do registro
            (<span className="mono">gab.anexo.dono_id</span>), que ainda não existe. Salve e
            reabra a ficha para enviar o PDF ou a imagem.
          </div>
        )}

      </Drawer>
    </form>
  );
}
